import { LimitWarning } from './LimitWarning';
import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { formatINR, formatNumber } from '@/lib/format';
import { ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-react';

const POLICY_ITEMS = [
    {
        key: 'refund',
        href: '/policies/refund',
        label: 'Refund Policy',
        prefix: 'I have read and accept the ',
        suffix: '.',
    },
    {
        key: 'privacy',
        href: '/policies/privacy',
        label: 'Privacy Policy',
        prefix: 'I accept the ',
        suffix: '.',
    },
    {
        key: 'fairuse',
        href: '/policies/fair-use',
        label: 'Fair Use Policy',
        prefix: 'I accept the ',
        suffix: ', understand digital goods are non-reversible, and confirm this is for entertainment use only.',
    },
];

function OrderSummary({ platformMeta, platformKey, accent, selectedPlan }) {
    if (!selectedPlan) {
        return (
            <div className="rounded-xl border border-border p-3 bg-secondary/40">
                <div className="text-xs text-muted-foreground">Order summary</div>
                <div className="mt-1 text-sm text-muted-foreground">No plan selected yet.</div>
            </div>
        );
    }
    return (
        <div className="rounded-xl border border-border p-3 bg-secondary/40">
            <div className="text-xs text-muted-foreground">Order summary</div>
            <div className="mt-1">
                <div className="text-sm capitalize text-foreground/80">
                    {platformMeta?.name} - {selectedPlan.label || ''}
                </div>
                <div
                    className="font-display text-xl font-semibold tabular-nums"
                    style={{ color: accent.color }}
                >
                    {formatNumber(selectedPlan.amount)}{' '}
                    <span className="text-xs uppercase text-muted-foreground">{platformMeta?.unit}</span>
                </div>
                {platformKey === 'olaparty' && (
                    <div className="text-xs text-amber-600 font-medium" data-testid="checkout-diamond-preview">
                        ≈ {(selectedPlan.amount / 200).toLocaleString(undefined, { maximumFractionDigits: 2 })} 💎 <span className="text-muted-foreground font-normal">(preview only)</span>
                    </div>
                )}
                <div className="text-lg font-display font-semibold tabular-nums">
                    {formatINR(selectedPlan.price_inr)}
                </div>
            </div>
        </div>
    );
}

function PolicyCheckboxes({ policy, onChange }) {
    return (
        <div className="space-y-2">
            {POLICY_ITEMS.map(({ key, href, label, prefix, suffix }) => (
                <label key={key} className="flex items-start gap-2 text-[13px] cursor-pointer">
                    <Checkbox
                        checked={!!policy[key]}
                        onCheckedChange={(v) => onChange(key, !!v)}
                        data-testid={`recharge-policy-${key}-checkbox`}
                    />
                    <span>
                        {prefix}
                        <a
                            className="text-[#1F9D8A] underline"
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {label}
                        </a>
                        {suffix}
                    </span>
                </label>
            ))}
        </div>
    );
}

/**
 * RechargeCheckoutCard
 * Bundles in-app ID input, order summary, policy checkboxes, and the Pay button.
 */
export function RechargeCheckoutCard({
    platformMeta,
    platformKey,
    accent,
    selectedPlan,
    inAppId,
    onInAppIdChange,
    policy,
    onPolicyChange,
    allPoliciesAccepted,
    loading,
    onPay,
    verifiable,
    verifying,
    recipientInfo,
    verifyError,
    onInAppIdBlur,
    gateways = [],
    gateway,
    onGatewayChange,
    maxLimit = 15000,
}) {
    const payGateway = gateways.find(g => g.key === gateway);
    const gatewayNotReady = payGateway && !payGateway.implemented;
    return (
        <Card className="mt-6 p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="inappid">Your {platformMeta?.name || ''} in-app ID</Label>
                    <Input
                        id="inappid"
                        data-testid="recharge-inapp-id-input"
                        placeholder={'Enter your in-app ID exactly'}
                        value={inAppId}
                        onChange={(e) => onInAppIdChange(e.target.value)}
                        onBlur={() => onInAppIdBlur && onInAppIdBlur()}
                    />
                    {verifiable && verifying && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="recharge-verifying">
                            <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                            Fetching account…
                        </div>
                    )}
                    {verifiable && !verifying && recipientInfo && (
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2" data-testid="recharge-recipient-info">
                            {recipientInfo.avatar
                                ? <img src={recipientInfo.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                                : <div className="h-8 w-8 rounded-full bg-emerald-200" />}
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-emerald-900 truncate" data-testid="recharge-recipient-nick">
                                    {recipientInfo.nick || 'Account found'}
                                </div>
                                <div className="text-[11px] text-emerald-700 font-mono">ID: {recipientInfo.vid || inAppId}</div>
                            </div>
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 ml-auto flex-shrink-0" />
                        </div>
                    )}
                    {verifiable && !verifying && !recipientInfo && verifyError && (
                        <div className="text-xs text-rose-600" data-testid="recharge-verify-error">{verifyError}</div>
                    )}
                    <div className="text-xs text-muted-foreground">
                        {verifiable
                            ? 'We auto-verify your ID to make sure diamonds reach the right account.'
                            : 'Make sure this is correct. Recharges cannot be reversed.'}
                    </div>
                </div>
                <OrderSummary platformMeta={platformMeta} platformKey={platformKey} accent={accent} selectedPlan={selectedPlan} />
            </div>

            {gateways.length > 1 && (
                <div className="mt-5" data-testid="gateway-selector">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pay using</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {gateways.map((g) => (
                            <button
                                key={g.key}
                                type="button"
                                onClick={() => onGatewayChange && onGatewayChange(g.key)}
                                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${gateway === g.key ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-foreground border-border hover:border-[#0E2A47]/40'}`}
                                data-testid={`gateway-option-${g.key}`}
                            >
                                {g.name}
                                {!g.implemented && <span className="ml-1.5 text-[10px] uppercase opacity-70">Coming soon</span>}
                            </button>
                        ))}
                    </div>
                    {gatewayNotReady && (
                        <div className="text-xs text-amber-600 mt-1" data-testid="gateway-not-ready">
                            {payGateway.name} isn't active yet — please pick Razorpay to complete your payment.
                        </div>
                    )}
                </div>
            )}

            <div className="mt-5 p-4 rounded-xl border border-border bg-secondary/30">
                <div className="flex items-start gap-2 text-[13px] mb-3">
                    <AlertCircle className="h-4 w-4 mt-0.5 text-[#B45309]" />
                    <div className="text-foreground/85">
                        Digital goods are non-reversible once credited. This service is for entertainment use only - not for gambling or money games.
                    </div>
                </div>
                <PolicyCheckboxes policy={policy} onChange={onPolicyChange} />
            </div>

            {selectedPlan && Number(selectedPlan.price_inr) > maxLimit && <LimitWarning testid="plan-limit-warning" amount={selectedPlan.price_inr} />}
            <Button
                disabled={loading || !selectedPlan || Number(selectedPlan.price_inr) > maxLimit || !inAppId.trim() || !allPoliciesAccepted || (verifiable && !recipientInfo) || gatewayNotReady}
                onClick={onPay}
                className="mt-5 w-full bg-[#0E2A47] hover:bg-[#0B223A] disabled:opacity-50"
                data-testid="recharge-pay-button"
            >
                {loading ? 'Processing...' : (
                    <span className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Pay {selectedPlan ? formatINR(selectedPlan.price_inr) : ''} with {payGateway ? payGateway.name : 'Razorpay'}
                    </span>
                )}
            </Button>
            {verifiable && !recipientInfo && inAppId.trim() && !verifying && (
                <div className="mt-2 text-[11px] text-amber-600 text-center" data-testid="recharge-verify-hint">
                    Verify your in-app ID above to enable payment.
                </div>
            )}
            {allPoliciesAccepted && (
                <div className="mt-2 text-[11px] text-muted-foreground text-center flex items-center gap-1 justify-center">
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Policies accepted
                </div>
            )}
        </Card>
    );
}

export default RechargeCheckoutCard;
